/**
 * Patient Dashboard — overview of appointments, prescriptions, and quick actions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { patientAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';

const PatientDashboard = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { on } = useNotifications();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await patientAPI.getDashboard();
      setDashboard(res.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
      toast.error(err?.message || 'Unable to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => on('prescription_ready', loadDashboard), [on, loadDashboard]);
  useEffect(() => on('consultation_ready', loadDashboard), [on, loadDashboard]);
  useEffect(() => on('appointment_update', loadDashboard), [on, loadDashboard]);

  const firstName = (userProfile?.full_name || 'Patient').split(' ')[0];

  if (loading) {
    return (
      <Layout title="Dashboard" role="patient">
        <div className="space-y-4 p-4">
          {[1,2,3].map(i => (
            <div key={i} className="skeleton h-28 rounded-2xl"></div>
          ))}
        </div>
      </Layout>
    );
  }

  const upcomingAppointments = dashboard?.upcoming_appointments || [];
  const notifications = dashboard?.notifications || [];

  return (
    <Layout title="Dashboard" role="patient">
      <div className="p-4 space-y-5 pb-24">
        {/* Greeting */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-5 text-white">
          <p className="text-primary-100 text-sm">Welcome back,</p>
          <h2 className="text-xl font-bold">{firstName} 👋</h2>
          <p className="text-primary-100 text-sm mt-1">
            {upcomingAppointments.length > 0
              ? `You have ${upcomingAppointments.length} upcoming appointment${upcomingAppointments.length > 1 ? 's' : ''}`
              : 'No upcoming appointments'}
          </p>
        </div>

        {/* Quick Actions */}
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => navigate('/patient/search-doctors')} className="card flex flex-col items-center gap-2 py-4 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <span className="text-sm font-medium">Find Doctor</span>
            </button>

            <button onClick={() => navigate('/patient/appointments/book')} className="card flex flex-col items-center gap-2 py-4 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-sm font-medium">Book Visit</span>
            </button>

            <button onClick={() => navigate('/patient/emergency')} className="card flex flex-col items-center gap-2 py-4 hover:shadow-lg transition-shadow border-red-100">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-red-600">Emergency</span>
            </button>

            <button onClick={() => navigate('/patient/prescriptions')} className="card flex flex-col items-center gap-2 py-4 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-sm font-medium">Prescriptions</span>
            </button>
          </div>
        </div>

        {/* Notifications Banner */}
        {notifications.length > 0 && (
          <Link to="/patient/notifications" className="block bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-yellow-200 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-yellow-800 font-bold text-sm">{notifications.length}</span>
              </div>
              <div>
                <p className="text-sm font-medium text-yellow-900">Unread Notifications</p>
                <p className="text-xs text-yellow-700">Tap to view all notifications</p>
              </div>
              <svg className="w-4 h-4 text-yellow-600 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        )}

        {/* Upcoming Appointments */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Upcoming Appointments</h3>
            <Link to="/patient/appointments" className="text-primary-500 text-sm font-medium">View All</Link>
          </div>

          {upcomingAppointments.length === 0 ? (
            <div className="card text-center py-8 text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">No upcoming appointments</p>
              <Link to="/patient/search-doctors" className="text-primary-500 text-sm font-medium mt-2 inline-block">Find a doctor</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.slice(0, 3).map(apt => (
                <div key={apt.id} className="card hover:shadow-md transition-shadow">
                  <Link to={`/patient/appointments/${apt.id}`} className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">Dr. {apt.doctor_name || 'Pending'}</p>
                      <p className="text-xs text-gray-500">{apt.type || 'Consultation'}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{apt.appointment_date || 'TBD'} • Queue #{apt.priority ?? '—'}</p>
                    </div>
                    <span className="badge-primary text-xs">{apt.status}</span>
                  </Link>
                  {apt.room_id && apt.status === 'in_progress' && (
                    <div className="mt-3 pt-3 border-t border-green-100 flex justify-end">
                      <button
                        onClick={() => navigate(`/consultation?room=${apt.room_id}&appointment=${apt.id}`)}
                        className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Join Video Call
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Prescriptions */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Recent Prescriptions</h3>
            <Link to="/patient/prescriptions" className="text-primary-500 text-sm font-medium">View All</Link>
          </div>

          {(dashboard?.recent_prescriptions || []).length === 0 ? (
            <div className="card text-center py-6 text-gray-400">
              <p className="text-sm">No prescriptions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(dashboard?.recent_prescriptions || []).slice(0, 2).map(rx => (
                <Link key={rx.id} to={`/patient/prescriptions/${rx.id}`} className="card flex items-center gap-3 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{rx.diagnosis || 'Prescription'}</p>
                    <p className="text-xs text-gray-500">Dr. {rx.doctor_name || 'Unknown'}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PatientDashboard;
